// src/screens/LoginScreen.js
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { IoLogoGoogle, IoLogoFacebook, IoLogoApple, IoEye, IoEyeOff } from 'react-icons/io5';
import { sendPasswordResetEmail, createUserWithEmailAndPassword } from 'firebase/auth'; // Added createUser
import { auth, db } from '../firebaseConfig'; // Ensure db is imported
// Added Firestore imports for migration
import { collection, query, where, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import kondifyLogo from '../assets/Kondify letra negra sin fondo.png';

function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [mensajeExito, setMensajeExito] = useState('');
  const [loading, setLoading] = useState(false);
  // NOTA: Dejamos 'navigate' por si acaso, pero no lo usamos para el login
  const { login, loginWithGoogle, loginWithFacebook, loginWithApple } = useAuth();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMensajeExito('');
    setLoading(true);
    try {
      await login(email, password);
      // ¡NO MÁS navigate('/')!
      // AuthContext se actualizará, y PublicRoute hará la navegación.
    } catch (err) {
      // --- LOGICA DE AUTO-REGISTRO PARA TEST USERS (HACK) ---
      if (err.code === 'auth/user-not-found' && email.includes('@email.com') && email.includes('residente')) {
        try {
          // Regex para validar "residente005@email.com"
          const match = email.match(/residente(\d{3})@email\.com/);
          if (match) {
            const num = match[1]; // ej "005"
            const expectedPass = `Residente${num}test`; // ej "Residente005test"

            if (password === expectedPass) {
              console.log(`Test User detectado (${email}). Creando cuenta Auth y migrando datos...`);

              // 1. Crear cuenta real en Firebase Auth
              const cred = await createUserWithEmailAndPassword(auth, email, password);
              const newUid = cred.user.uid;

              // 2. Buscar si existe data "fake" (test_uid_XXX) en Firestore
              const q = query(collection(db, "usuarios"), where("email", "==", email));
              const querySnapshot = await getDocs(q);

              if (!querySnapshot.empty) {
                const oldDoc = querySnapshot.docs[0];
                const oldData = oldDoc.data();

                // 3. Crear nuevo doc con UID real
                await setDoc(doc(db, "usuarios", newUid), {
                  ...oldData,
                  uid: newUid,
                  fromTestMigration: true
                });

                // 4. Borrar doc viejo "test_uid_XXX"
                await deleteDoc(oldDoc.ref);
                console.log("Migración completada. Login exitoso.");
                return; // Terminamos, el AuthContext detectará el login
              } else {
                // Si no habia data (raro), al menos ya creamos el user
                console.log("Usuario creado, pero no había data previa en Firestore.");
                return;
              }
            }
          }
        } catch (devErr) {
          console.error("Error en auto-registro dev:", devErr);
          // Si falla esto, caemos al error normal
        }
      }

      setError('Error al iniciar sesión. Revisa tu email y contraseña.');
      console.error(err);
      setLoading(false); // ¡Importante! Si falla, re-habilita el botón
    }
    // No necesitamos setLoading(false) aquí, porque si tiene éxito, 
    // este componente se "desmontará" (desaparecerá)
  };

  const handleSocialLogin = async (provider) => {
    setError('');
    setMensajeExito('');
    setLoading(true);
    try {
      if (provider === 'google') {
        await loginWithGoogle();
      } else if (provider === 'facebook') {
        await loginWithFacebook();
      } else if (provider === 'apple') {
        await loginWithApple();
      }
      // ¡NO MÁS navigate!
      // AuthContext y las rutas protegidas/públicas se encargarán
      // de decidir si ir a '/' o a '/completar-registro'
    } catch (err) {
      setError('Error al iniciar sesión con redes sociales.');
      console.error(err);
      setLoading(false); // Re-habilita si falla
    }
  };

  const handlePasswordReset = async () => {
    // ... (esta función de reseteo no cambia)
    setError('');
    setMensajeExito('');
    const emailInput = window.prompt("Por favor, ingresa tu email para enviarte el enlace de recuperación:");
    if (!emailInput) {
      return;
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, emailInput);
      setMensajeExito("¡Hecho! Revisa tu correo (incluso spam) para ver el enlace de recuperación.");
    } catch (err) {
      console.error(err);
      setError("Error al enviar el correo. Asegúrate de que el email sea correcto.");
    }
    setLoading(false);
  };

  return (
    <div className="form-container" style={{ maxWidth: '450px', padding: '40px 30px' }}>
      {/* Brand Header with Kondify Logo */}
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <img 
          src={kondifyLogo} 
          alt="Kondify" 
          style={{ width: '160px', height: 'auto', objectFit: 'contain', marginBottom: '12px' }} 
        />
        <h1 className="form-header" style={{ fontSize: '1.4rem', fontWeight: '800', margin: 0, color: '#1e293b', fontFamily: 'Manrope, sans-serif' }}>
          Iniciar Sesión
        </h1>
        <p style={{ fontSize: '0.88rem', color: '#64748b', marginTop: '6px', marginBottom: 0 }}>
          Ingresa tus credenciales para acceder a tu hogar
        </p>
      </div>

      {error && <p className="form-mensaje error">{error}</p>}
      {mensajeExito && <p className="form-mensaje" style={{ backgroundColor: '#d4edda', color: '#155724' }}>{mensajeExito}</p>}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <div className="form-group">
          <label className="form-label" style={{ fontWeight: '700', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
            Email
          </label>
          <input
            type="email"
            className="form-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ borderRadius: '14px', padding: '14px' }}
          />
        </div>
        <div className="form-group">
          <label className="form-label" style={{ fontWeight: '700', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
            Contraseña
          </label>
          <div className="password-input-container">
            <input
              type={showPassword ? 'text' : 'password'}
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{ borderRadius: '14px', padding: '14px 44px 14px 14px' }}
            />
            <button
              type="button"
              className="password-toggle-button"
              onClick={() => setShowPassword(!showPassword)}
              style={{ right: '14px' }}
            >
              {showPassword ? <IoEyeOff /> : <IoEye />}
            </button>
          </div>
        </div>
        <div style={{ textAlign: 'right', marginTop: '-8px', marginBottom: '8px' }}>
          <button
            type="button"
            onClick={handlePasswordReset}
            className="form-link-button"
            style={{ fontSize: '0.82rem', fontWeight: '700', color: '#6A55DC' }}
          >
            ¿Olvidaste tu contraseña?
          </button>
        </div>

        <button 
          type="submit" 
          className="form-button btn-kondo" 
          disabled={loading}
          style={{ padding: '16px', borderRadius: '16px', fontWeight: '700', fontSize: '1rem', background: '#7B61FF', boxShadow: '0 8px 16px rgba(123, 97, 255, 0.2)' }}
        >
          {loading ? 'Iniciando...' : 'Iniciar Sesión'}
        </button>
      </form>

      <p style={{ textAlign: 'center', marginTop: '28px', fontSize: '0.9rem', color: '#64748b' }}>
        ¿No tienes una cuenta? <Link to="/registro" style={{ color: '#7B61FF', fontWeight: 'bold', textDecoration: 'none' }}>Regístrate aquí</Link>
      </p>

      <div style={{ marginTop: '30px', borderTop: '1px solid #f1f5f9', paddingTop: '20px', textAlign: 'center' }}>
        <p style={{ fontSize: '0.85em', color: '#64748b', marginBottom: '8px' }}>¿Quieres ofrecer tus servicios?</p>
        <Link to="/registro-proveedor-externo" style={{ color: '#7B61FF', fontWeight: 'bold', textDecoration: 'none', fontSize: '0.92rem' }}>
          Vende en Kondify
        </Link>
      </div>

    </div>
  );
}

export default LoginScreen;
